import { useEffect, useRef, useState } from "react";

import api from "@helpers/api";

/**
 * Resolves the mentoring program behind a split entry of type "mentoring".
 *
 * The split rule only carries the hashed `mentoring_enrollment_id`; the program
 * name + banner live on the creator's enrollment detail. The split page is
 * owner-only, and a mentoring entry is only ever injected into the split of the
 * community the creator attached — so the viewer (community owner) is the
 * enrollment's `user_id`, and GET /mentoring/my-enrollments/{enrollmentId}
 * (scoped to the auth user) resolves it. A failed fetch (e.g. ownership was
 * transferred after attach) degrades to the card's fallback rather than caching.
 *
 * A module-level cache dedupes SUCCESSFUL fetches across React Flow node
 * re-renders / remounts — the program data is immutable for the page's lifetime.
 * Failures are intentionally not cached so a later remount can retry.
 */
const cache = new Map(); // enrollmentId -> { name, banner: { url, type } } | null

export default function useMentoringProgram(enrollmentId) {
  const [program, setProgram] = useState(() => cache.get(enrollmentId) ?? null);
  const [loading, setLoading] = useState(() => Boolean(enrollmentId) && !cache.has(enrollmentId));

  const requestRef = useRef(0);

  useEffect(() => {
    if (!enrollmentId) {
      setProgram(null);
      setLoading(false);
      return;
    }

    if (cache.has(enrollmentId)) {
      setProgram(cache.get(enrollmentId));
      setLoading(false);
      return;
    }

    const requestId = ++requestRef.current;
    setLoading(true);

    (async () => {
      const res = await api.get(`mentoring/my-enrollments/${enrollmentId}`);
      if (requestId !== requestRef.current) return;

      // A failed request (the api helper returns the axios error) must NOT be
      // cached — otherwise a transient error would permanently hide the banner.
      if (res?.response) {
        setProgram(null);
        setLoading(false);
        return;
      }

      const raw = res?.data?.program;
      const resolved = raw
        ? {
            name: raw.name ?? null,
            banner: { url: raw.banner_url ?? null, type: raw.banner_type ?? "image" },
          }
        : null;

      cache.set(enrollmentId, resolved);
      setProgram(resolved);
      setLoading(false);
    })();
  }, [enrollmentId]);

  return { program, loading };
}
